import { useEffect, useState, useRef, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';
import { resolveClinicLogoUrl } from '@/lib/clinicBranding';
import { useClinic, useUpdateClinicSettings } from '@/hooks/useClinicSettings';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import {
  SaveBar,
  SectionCard,
  SettingsFormGrid,
  SettingsFullField,
  SettingsHint,
  SettingsLoading,
  SettingsStack,
} from './settingsChrome';
import type { Json } from '@/types/database.generated';

interface BrandingShape {
  logo_url: string;
  primary_color: string;
}

const EMPTY_BRANDING: BrandingShape = { logo_url: '', primary_color: '' };

const ALLOWED_LOGO_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/svg+xml', 'image/webp'];
const MAX_LOGO_SIZE = 5 * 1024 * 1024; // 5 MB

function readBranding(value: Json): BrandingShape {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const v = value as Record<string, Json>;
    return {
      logo_url: typeof v.logo_url === 'string' ? v.logo_url : '',
      primary_color: typeof v.primary_color === 'string' ? v.primary_color : '',
    };
  }
  return EMPTY_BRANDING;
}

export function ClinicBranding() {
  const { t } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const { isClinicAdmin, isPlatformAdmin } = useAuth();
  const { data: clinic, isLoading, error } = useClinic(activeClinicId);
  const update = useUpdateClinicSettings(activeClinicId);

  const canEdit = Boolean(activeClinicId && (isPlatformAdmin || isClinicAdmin(activeClinicId)));

  const [logoUrl, setLogoUrl] = useState('');
  const [primaryColor, setPrimaryColor] = useState('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const applyClinic = (row: NonNullable<typeof clinic>) => {
    const b = readBranding(row.branding);
    setLogoUrl(b.logo_url);
    setPrimaryColor(b.primary_color);
    setLogoPreview(resolveClinicLogoUrl(b.logo_url) ?? '');
    setLogoFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  useEffect(() => {
    if (!clinic) return;
    applyClinic(clinic);
    // Seed from the loaded clinic row only; applyClinic closes over setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinic]);

  if (isLoading) return <SettingsLoading />;
  if (error) return <ErrorAlert message={t('settings.load_error')} />;
  if (!clinic) return <ErrorAlert message={t('settings.no_clinic')} />;

  const handleLogoFile = (file: File | null) => {
    if (!file) {
      setLogoFile(null);
      setLogoPreview('');
      return;
    }

    if (!ALLOWED_LOGO_TYPES.includes(file.type)) {
      toast.error(t('settings.branding.invalid_logo_type'));
      return;
    }

    if (file.size > MAX_LOGO_SIZE) {
      toast.error(t('settings.branding.logo_too_large'));
      return;
    }

    setLogoFile(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    handleLogoFile(file);
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview('');
    setLogoUrl('');
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!activeClinicId) return;

    try {
      let finalLogoUrl = logoUrl.trim();

      if (logoFile) {
        setUploadingLogo(true);

        const ext = logoFile.name.split('.').pop() || 'png';
        const storagePath = `${activeClinicId}/logo.${ext}`;

        const { error: uploadError } = await supabase.storage
          .from('clinic-logos')
          .upload(storagePath, logoFile, {
            upsert: true,
            contentType: logoFile.type,
          });

        if (uploadError) {
          throw new Error(`Upload failed: ${uploadError.message}`);
        }

        finalLogoUrl = `clinic-logos/${storagePath}`;
        setUploadingLogo(false);
      }

      const branding: BrandingShape = {
        logo_url: finalLogoUrl,
        primary_color: primaryColor.trim(),
      };

      await update.mutateAsync({ branding: branding as unknown as Json });
      toast.success(t('settings.saved_toast'));

      setLogoFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      console.error('update_clinic_settings (branding) failed', err);
      toast.error(t('settings.save_error_toast'));
      setUploadingLogo(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="flex min-w-0 flex-col gap-s4">
      <SectionCard
        title={t('settings.branding.title')}
        subtitle={t('settings.branding.subtitle')}
        readOnly={!canEdit}
      >
        <SettingsFormGrid className="mt-0">
          <SettingsFullField>
            <SettingsStack className="gap-s3">
              <label className={cn(TYPE.label, 'text-ds-text')} htmlFor="branding-logo">
                {t('settings.branding.logo_upload')}
              </label>
              <input
                id="branding-logo"
                ref={fileInputRef}
                type="file"
                accept={ALLOWED_LOGO_TYPES.join(',')}
                onChange={handleFileInputChange}
                disabled={!canEdit}
                className={cn(
                  TYPE.small,
                  'block w-full min-w-0 text-ds-muted file:mr-s3 file:rounded-ctl file:border-0 file:bg-ds-teal-tint file:px-s4 file:py-s2 file:font-semibold file:text-ds-teal hover:file:bg-ds-teal-tint disabled:opacity-60',
                )}
              />
              <SettingsHint>{t('settings.branding.logo_upload_hint')}</SettingsHint>

              {logoPreview ? (
                <div className="relative inline-block">
                  <div className="logo-plate h-24 w-24 overflow-hidden">
                    <img
                      src={logoPreview}
                      alt=""
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                  {canEdit ? (
                    <Button
                      type="button"
                      variant="danger"
                      size="icon"
                      className="absolute -right-2 -top-2"
                      onClick={handleRemoveLogo}
                      aria-label={t('common.delete')}
                    >
                      <X />
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </SettingsStack>
          </SettingsFullField>

          <SettingsFullField>
            <div>
              <label className={cn(TYPE.label, 'text-ds-text')} htmlFor="branding-color">
                {t('settings.branding.primary_color')}
              </label>
              <div className="mt-s2 flex min-w-0 items-center gap-s3">
                <input
                  id="branding-color"
                  type="color"
                  value={primaryColor || '#1B6B6B'}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  disabled={!canEdit}
                  className="h-10 w-14 shrink-0 cursor-pointer rounded-ctl border border-ds-field bg-ds-card disabled:opacity-60"
                />
                <Input
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  disabled={!canEdit}
                  placeholder="#1B6B6B"
                  className="font-mono"
                />
              </div>
              <SettingsHint className="mt-s2">
                {t('settings.branding.primary_color_hint')}
              </SettingsHint>
            </div>
          </SettingsFullField>
        </SettingsFormGrid>
      </SectionCard>

      <SaveBar
        canEdit={canEdit}
        pending={update.isPending || uploadingLogo}
        onCancel={() => applyClinic(clinic)}
      />
    </form>
  );
}
